import { registerComponent, registerSystem } from 'aframe';
import { Object3D } from 'three';

var _a;
/** A class that describes the methods Aframe will inject into the component at runtime. */
var BaseComponent = (_a = /** @class */ (function () {
        function class_1() {
        }
        return class_1;
    }()),
    _a.schema = {},
    _a);

/** Convert a ComponentClass into a ComponentDefinition for Aframe. */
function toComponent(ComponentClass) {
    var prototype = ComponentClass.prototype;
    /** The final component object. */
    var component = {
        schema: ComponentClass.schema,
        multiple: ComponentClass.multiple,
        dependencies: ComponentClass.dependencies,
    };
    /** Methods to copy over to the object. */
    var methodKeys = Object.getOwnPropertyNames(prototype);
    // Move methods onto the component object.
    methodKeys.forEach(function (key) {
        if (key !== "constructor") {
            component[key] = prototype[key];
        }
    });
    /** A reference to the original init method, to be called after property initialization. */
    var onInit = prototype.init;
    // Override the init method to make a new instance of the class, and pass the properties to the component.
    component.init = function (data) {
        var _this = this;
        var instance = new ComponentClass();
        var propertyKeys = Object.getOwnPropertyNames(instance);
        // Pass new instance values to the component instance.
        propertyKeys.forEach(function (key) {
            _this[key] = instance[key];
        });
        // Bind any methods marked with @bind.
        methodKeys.forEach(function (key) {
            var method = _this[key];
            if (method._bindToAframe && typeof method === "function") {
                _this[key] = method.bind(_this);
            }
        });
        if (onInit) {
            onInit.call(this, data);
        }
        // Bind evens to the component instance.
        if (ComponentClass.bindEvents !== false) {
            for (var eventName in this.events) {
                this.events[eventName] = this.events[eventName].bind(this);
            }
        }
    };
    return component;
}
/** Attaches event listeners like Aframe does when a component is playing. */
function attachEvents(component) {
    for (var eventName in component.events) {
        component.el.addEventListener(eventName, component.events[eventName]);
    }
}

/** Decorator to register the class as an Aframe component. */
function component(name) {
    return function (target) {
        registerComponent(name, toComponent(target));
    };
}

var _a$1;
/** A class that describes the methods Aframe will inject into the system at runtime. */
var BaseSystem = (_a$1 = /** @class */ (function () {
        function class_1() {
        }
        return class_1;
    }()),
    _a$1.schema = {},
    _a$1);

/** Convert a SystemClass into a SystemDefinition for Aframe. */
function toSystem(SystemClass) {
    var prototype = SystemClass.prototype;
    /** The final system object. */
    var system = {
        schema: SystemClass.schema,
    };
    /** Methods to copy over to the object. */
    var methodKeys = Object.getOwnPropertyNames(prototype);
    // Move methods onto the system object.
    methodKeys.forEach(function (key) {
        if (key !== "constructor") {
            system[key] = prototype[key];
        }
    });
    /** A reference to the original init method, to be called after property initialization. */
    var onInit = prototype.init;
    // Override the init method to make a new instance of the class, and pass the properties to the system.
    system.init = function () {
        var _this = this;
        var instance = new SystemClass();
        var propertyKeys = Object.getOwnPropertyNames(instance);
        // Pass new instance values to the system instance.
        propertyKeys.forEach(function (key) {
            _this[key] = instance[key];
        });
        // Bind any methods marked with @bind.
        methodKeys.forEach(function (key) {
            var method = _this[key];
            if (method._bindToAframe && typeof method === "function") {
                _this[key] = method.bind(_this);
            }
        });
        if (onInit) {
            onInit.call(this);
        }
    };
    return system;
}

/** Decorator to register a class as an Aframe system. */
function system(name) {
    return function (target) {
        registerSystem(name, toSystem(target));
    };
}

/** Property decorator to bind the method to the component instance. */
function bind(target, propertyKey, descriptor) {
    if (typeof (descriptor === null || descriptor === void 0 ? void 0 : descriptor.value) !== "function") {
        throw new TypeError("Only methods can be decorated with @bind. <" + propertyKey + "> is not a method!");
    }
    descriptor.value._bindToAframe = true;
}

/**
 * Given a component instance, inject common properties, attach event listeners
 * and call init. Used for testing.
 */
function initializeComponentInstance(component, initialData) {
    // Set up a valid element.
    component.el = document.createElement("a-entity");
    component.el.object3D = new Object3D();
    component.el.sceneEl = document.createElement("a-scene");
    // Initialize with initial data.
    component.data = initialData;
    component.init(initialData);
    // Attach event listeners as if the component was playing.
    attachEvents(component);
    return component;
}
/**
 * Given a component class, create an initialized component instance.
 * Used for testing.
 */
function initializeTestComponent(ComponentClass, initialData) {
    var component = toComponent(ComponentClass);
    var instance = Object.create(component);
    return initializeComponentInstance(instance, initialData);
}
/**
 * Given a system instance, inject common properties, attach event listeners
 * and call init. Used for testing.
 */
function initializeSystemInstance(system, initialData) {
    system.el = document.createElement("a-scene");
    // Initialize with initial data.
    if (initialData) {
        system.data = initialData;
    }
    system.init();
    return system;
}
/**
 * Given a system class, create an initialized system instance.
 * Used for testing.
 */
function initializeTestSystem(SystemClass, initialData) {
    var system = toSystem(SystemClass);
    var instance = Object.create(system);
    return initializeSystemInstance(instance, initialData);
}

export { BaseComponent, BaseSystem, attachEvents, bind, component, initializeComponentInstance, initializeSystemInstance, initializeTestComponent, initializeTestSystem, system, toComponent, toSystem };
//# sourceMappingURL=aframe-typescript-class-components.es.js.map
